import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { axiosObj } from "../../../config/apiClients";
import { OnlineUser, OnlineUserChange } from "../../../utils/types";
import { OnlineUsersState } from "../../types";

const initialState: OnlineUsersState = {
  isLoading: false,
  users: [],
  changesWhileLoading: [],
  error: null,
};

// Who is online right now; socket events keep the list current after this
export const getOnlineUsers = createAsyncThunk("/user/online", async (_, { rejectWithValue }) => {
  try {
    const response = await axiosObj.get("user/online");
    return response.data;
  } catch (error) {
    return rejectWithValue(error || { message: "Failed to Fetch Online Users" });
  }
});

const applyChange = (users: OnlineUser[], change: OnlineUserChange) => {
  // Remove the user first, so a repeated "joined" (reconnect) replaces the row instead of duplicating it
  const otherUsers = users.filter((user) => user.userId !== getChangedUserId(change));
  if (change.type === "joined") otherUsers.push(change.user);
  return otherUsers;
};

const getChangedUserId = (change: OnlineUserChange) =>
  change.type === "joined" ? change.user.userId : change.userId;

const onlineUsersSlice = createSlice({
  name: "onlineUsers",
  initialState,
  reducers: {
    onlineUserChanged: (state, action: PayloadAction<OnlineUserChange>) => {
      // The snapshot being loaded may not include this change yet; apply it once the snapshot arrives
      if (state.isLoading) {
        state.changesWhileLoading.push(action.payload);
        return;
      }
      state.users = applyChange(state.users, action.payload);
    },
    clearOnlineUsers: () => initialState,
  },
  extraReducers(builder) {
    builder
      .addCase(getOnlineUsers.pending, (state) => {
        state.isLoading = true;
        state.error = null;
        state.changesWhileLoading = [];
      })
      .addCase(getOnlineUsers.fulfilled, (state, action) => {
        let users: OnlineUser[] = action.payload.payload.users;
        for (const change of state.changesWhileLoading) {
          users = applyChange(users, change);
        }
        state.users = users;
        state.changesWhileLoading = [];
        state.isLoading = false;
      })
      .addCase(getOnlineUsers.rejected, (state, action) => {
        state.isLoading = false;
        state.users = [];
        state.changesWhileLoading = [];
        state.error = (action.payload as { message?: string })?.message ?? "Failed to Fetch Online Users";
      });
  },
});

export const { onlineUserChanged, clearOnlineUsers } = onlineUsersSlice.actions;

export default onlineUsersSlice.reducer;
